import {
  BILLING_ROUTES_LEGACY,
  LEGACY_PORTAL_ROUTE_REDIRECTS,
  LEGACY_ROUTE_REDIRECTS,
  ROUTES,
} from '../routes';

/**
 * Query keys that identify a sibling view on a given path. Extra values here
 * keep a destination from matching (e.g. commissions `view=porcentajes` vs `status=all`).
 * Filter/pagination noise (`page`, `step`, `pais`…) is ignored.
 */
const PATH_DISCRIMINATORS: Record<string, readonly string[]> = {
  [ROUTES.quote]: ['view'],
  [ROUTES.operations]: ['view'],
  [ROUTES.accounting]: ['view'],
  [ROUTES.commissions]: ['status', 'view'],
  [ROUTES.settings]: ['tab'],
  [ROUTES.audit]: ['tab'],
  [ROUTES.backup]: ['view'],
  [ROUTES.profile]: ['seccion'],
};

const PATH_QUERY_DEFAULTS: Record<string, Record<string, string>> = {
  [ROUTES.quote]: { view: 'history' },
  [ROUTES.operations]: { view: 'history' },
  [ROUTES.accounting]: { view: 'logistrade' },
  [ROUTES.commissions]: { status: 'all' },
  [ROUTES.settings]: { tab: 'empresa' },
  [ROUTES.audit]: { tab: 'logs' },
  [ROUTES.backup]: { view: 'datos' },
  [ROUTES.profile]: { seccion: 'cuenta' },
};

/** Same map as pages/Settings.tsx — leftover tab ids still render a current section. */
const SETTINGS_TAB_ALIASES: Record<string, string> = {
  general: 'empresa',
  quote: 'empresa',
  visual: 'appearance',
  apariencia: 'appearance',
  integrations: 'integraciones',
  siigo: 'integraciones',
  'payment-gateways': 'integraciones',
  whatsapp: 'comunicaciones',
  security: 'sistema',
  migration: 'sistema',
};

const SETTINGS_TABS = new Set(['inicio', 'empresa', 'appearance', 'integraciones', 'comunicaciones', 'sistema']);

export function canonicalizePathname(pathname: string): string {
  let path = pathname;

  for (const [legacy, modern] of Object.entries(LEGACY_ROUTE_REDIRECTS)) {
    if (path === legacy) {
      path = modern;
      break;
    }
    if (path.startsWith(`${legacy}/`)) {
      path = modern + path.slice(legacy.length);
      break;
    }
  }

  if (path.startsWith(`${ROUTES.billingReport}/`)) {
    const rest = path.slice(ROUTES.billingReport.length + 1);
    const [first, ...tail] = rest.split('/');
    const mapped = BILLING_ROUTES_LEGACY[first] ?? first;
    path = tail.length > 0
      ? `${ROUTES.billingReport}/${mapped}/${tail.join('/')}`
      : `${ROUTES.billingReport}/${mapped}`;
  }

  if (path.startsWith(`${ROUTES.clientPortal}/`)) {
    const rest = path.slice(ROUTES.clientPortal.length + 1);
    const [first, ...tail] = rest.split('/');
    const mapped = LEGACY_PORTAL_ROUTE_REDIRECTS[first] ?? first;
    path = tail.length > 0
      ? `${ROUTES.clientPortal}/${mapped}/${tail.join('/')}`
      : `${ROUTES.clientPortal}/${mapped}`;
  }

  return path;
}

function canonicalizeQueryValue(path: string, key: string, value: string | null): string | null {
  if (value == null) return null;
  if (path === ROUTES.settings && key === 'tab') {
    if (SETTINGS_TABS.has(value)) return value;
    return SETTINGS_TAB_ALIASES[value] ?? 'empresa';
  }
  return value;
}

function pathMatches(destPath: string, currentPath: string): boolean {
  if (destPath === ROUTES.home) return currentPath === ROUTES.home;
  return currentPath === destPath || currentPath.startsWith(`${destPath}/`);
}

export function isNavDestinationActive(destination: string, pathname: string, search: string): boolean {
  const currentPath = canonicalizePathname(pathname);
  const [rawPath, rawQuery = ''] = destination.split('?');
  const destPath = canonicalizePathname(rawPath);

  if (!rawQuery) {
    return pathMatches(destPath, currentPath);
  }

  if (currentPath !== destPath) return false;

  const destParams = new URLSearchParams(rawQuery);
  const currentParams = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const defaults = PATH_QUERY_DEFAULTS[destPath] ?? {};
  const discriminators = PATH_DISCRIMINATORS[destPath] ?? [...new Set([...destParams.keys(), ...Object.keys(defaults)])];

  for (const key of discriminators) {
    const destDeclared = destParams.has(key);
    const inDefaults = key in defaults;
    const expectedRaw = destDeclared ? destParams.get(key) : (inDefaults ? defaults[key] : null);
    const actualRaw = currentParams.get(key) ?? (inDefaults ? defaults[key] : null);
    const expected = canonicalizeQueryValue(destPath, key, expectedRaw);
    const actual = canonicalizeQueryValue(destPath, key, actualRaw);

    if (destDeclared || inDefaults) {
      if (actual !== expected) return false;
      continue;
    }

    if (actual != null) return false;
  }

  return true;
}

export function isNavParentActive(
  item: { id: string; subItems?: { id: string }[] },
  pathname: string,
  search: string,
): boolean {
  if (item.subItems?.some((sub) => isNavDestinationActive(sub.id, pathname, search))) {
    return true;
  }
  return isNavDestinationActive(item.id, pathname, search);
}
